<script lang="ts" setup>
/* eslint-disable vue/no-v-html */
import type { DropdownsStateKeys } from '~/composables/states'
import type { ApiSearchAutocomplete } from '~~/server/api/search/autocomplete'

export interface SearchProps {
    showSuggestions?: boolean
    showControlsOnMobile?: boolean
}

const route = useRoute()
const timeoutId = ref<NodeJS.Timeout>()
const query = ref<string>(route.query?.q?.toString() || '')
const apiCallDelay = ref<number>(150) // in miliseconds
const result = ref<ApiSearchAutocomplete>()
const minQueryLength = 2
const history = ref<string[]>([])
const dropdownsOpenState = useDropdowns()
const modalOpen = ref<boolean>(false)
const mobileInput = ref<HTMLElement>()
const formAction = ref<VNuxtLinkTo>(useNamedRoutes({ name: 'search' }))
const props = defineProps<SearchProps>()
const { data, sendEvent } = useTracker()
const { $t } = useNuxtApp()

const showResults = computed<boolean>(
    (): boolean =>
        query.value.length > minQueryLength && result?.value !== undefined
)

const totalResultsCount = computed(() =>
    result.value?.reduce((sum, cat) => sum + cat.data.length, 0) ?? 0
)

// Accessibility: Track dropdown state for screen reader announcements (WCAG 4.1.2)
const dropdownAnnouncement = ref<string>('')
let lastAnnouncedState = false

// Watch for dropdown state changes and announce them
watch(() => dropdownsOpenState.value.search, (isOpen) => {
    if (isOpen !== lastAnnouncedState) {
        lastAnnouncedState = isOpen
        if (isOpen) {
            // Announce dropdown opened
            nextTick(() => {
                if (showResults.value && totalResultsCount.value > 0) {
                    dropdownAnnouncement.value = `${$t('Vyhľadávanie otvorené.')} ${$t('Nájdených')} ${totalResultsCount.value} ${$t('výsledkov.')} ${$t('Použite šípky pre navigáciu.')}`
                } else {
                    dropdownAnnouncement.value = $t('Vyhľadávanie otvorené. Zadajte hľadané slovo.') as string
                }
            })
        } else {
            dropdownAnnouncement.value = $t('Vyhľadávanie zatvorené.') as string
        }
    }
})

// Also announce when results change while dropdown is open
watch(totalResultsCount, (count) => {
    if (dropdownsOpenState.value.search && count > 0) {
        dropdownAnnouncement.value = ''
        nextTick(() => {
            dropdownAnnouncement.value = `${$t('Nájdených')} ${count} ${$t('výsledkov.')} ${$t('Použite šípky pre navigáciu.')}`
        })
    }
})

type EventPropsType = {
    click?: string
    event?: string
}

const handleSendEvent = (eventProps: EventPropsType): void => {
    sendEvent({
        data: {
            ...data,
            ...eventProps,
        },
    })
}

const handleKeyUp = (e?: KeyboardEvent): void => {
    if (!props.showSuggestions || e?.code === 'Escape') return
    // clear prev timeout
    if (timeoutId.value) clearTimeout(timeoutId.value)
    // query length
    if (query.value.length <= minQueryLength) return
    // start api call delay
    timeoutId.value = setTimeout(async () => {
        const response = await $fetch('/api/search/autocomplete', {
            params: {
                query: query.value,
            },
        })
        result.value = response
        dropdownsOpenState.value.search = true
    }, apiCallDelay.value)
}

const handleFocus = (): void => {
    // handle send event
    handleSendEvent({
        click: 'open',
    })

    // close all opened dropdowns
    Object.keys(dropdownsOpenState.value).forEach(
        (key) => (dropdownsOpenState.value[key as DropdownsStateKeys] = false)
    )
    // handle key press
    handleKeyUp()
    // open search
    dropdownsOpenState.value.search = true
}

const handleClickOutside = (): void => {
    // handle send event
    if (dropdownsOpenState.value.search) {
        handleSendEvent({
            click: 'close',
        })
    }

    dropdownsOpenState.value.search = false
    modalOpen.value = false
    result.value = undefined
}

const handleClickOnDropdownContent = (e: Event): void => {
    // stop propagation
    e.stopPropagation()
}

const handleSubmit = (e: Event): void => {
    // empty query?
    if (query.value.length < minQueryLength) {
        e.preventDefault()
        return
    }

    // handle send event
    handleSendEvent({
        event: 'submit',
    })

    // close modal
    dropdownsOpenState.value.search = false
    // query already in search history?
    if (!history.value?.some((value) => value === query.value)) {
        // save query to the history
        sessionStorage.setItem(
            'search-history',
            JSON.stringify([...history.value, query.value])
        )
    }
}
const handleEscKeyDown = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape') return
    // Only react when the search dropdown is actually open — otherwise we'd
    // swallow Escape for unrelated components (modals, lightbox, ...).
    if (!dropdownsOpenState.value.search) return
    e.stopImmediatePropagation()

    handleSendEvent({
        click: 'close',
    })

    dropdownsOpenState.value.search = false
}

watchEffect(() => {
    if (mobileInput.value) mobileInput.value.focus()
})

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleEscKeyDown)
    // set history queries from session storage
    if (sessionStorage.getItem('search-history')) {
        history.value = JSON.parse(
            sessionStorage?.getItem('search-history') || ''
        )
        return
    }
    history.value = []
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleEscKeyDown)
})
</script>
<template>
  <form
    method="GET"
    :action="formAction"
    class="search-form d-block d-md-flex"
    :class="{
      'position-relative': props.showControlsOnMobile,
      focused: dropdownsOpenState.search,
    }"
    @click="handleClickOnDropdownContent"
    @submit="handleSubmit"
  >
    <input
      v-if="!modalOpen"
      id="q"
      v-model="query"
      role="combobox"
      :aria-expanded="dropdownsOpenState.search"
      aria-controls="search-results-listbox"
      aria-haspopup="listbox"
      aria-autocomplete="list"
      class="search-control border-0 w-100 px-4 pe-md-10"
      :class="{
        'd-none d-md-block': !props.showControlsOnMobile,
        'd-block': props.showControlsOnMobile,
      }"
      type="text"
      :aria-label="$t('Vyhľadávanie')"
      :placeholder="$t('Vyhľadávanie')"
      autocomplete="off"
      spellcheck="false"
      autocorrect="off"
      name="q"
      tabindex="0"
      @focus="handleFocus"
      @keyup="handleKeyUp"
    >
    <SearchAutocompleteButtonSubmit
      tabindex="-1"
      :class="{
        'd-none d-md-block me-md-2': !props.showControlsOnMobile,
        'd-block me-4': props.showControlsOnMobile,
      }"
      class="search-submit border-0 p-0 m-0 d-block"
      :label="$t('Hľadaj')"
      type="submit"
    />
    <SearchAutocompleteButtonSubmit
      tabindex="-1"
      :class="{
        'd-block d-md-none': !props.showControlsOnMobile,
        'd-none': props.showControlsOnMobile,
      }"
      class="search-submit start-0 border-0 p-0 m-0 ms-4"
      :label="$t('Otvoriť vyhľadávanie')"
      type="button"
      @touchend.prevent="modalOpen = true"
    />

    <div
      v-if="modalOpen && !props.showControlsOnMobile"
      class="search-modal position-absolute start-0 top-0 end-0 bottom-0 bg-white px-2 d-flex align-items-center z-index-10"
    >
      <input
        id="q"
        ref="mobileInput"
        v-model="query"
        role="combobox"
        :aria-expanded="dropdownsOpenState.search"
        aria-controls="search-results-listbox"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        class="d-block search-control ps-8 pe-10 flex-grow-1 fs-bigger border-0"
        type="text"
        :aria-label="$t('Vyhľadávanie')"
        :placeholder="$t('Vyhľadávanie')"
        autocomplete="off"
        spellcheck="false"
        autocorrect="off"
        name="q"
        autofocus
        @focus="handleFocus"
        @keydown="handleKeyUp"
      >
      <SearchAutocompleteButtonClose
        class="search-close d-block bg-transparent border-0 p-0 text-black-1 position-absolute me-4 end-0"
        @touchend.prevent="handleClickOutside"
      />
      <SearchAutocompleteButtonSubmit
        class="search-submit d-block bg-transparent border-0 p-0 text-black-1 position-absolute"
        :label="$t('Hľadaj')"
        type="submit"
      />
    </div>

    <SearchAutocompleteDropdown v-if="dropdownsOpenState.search">
      <SearchAutocompleteResults
        v-if="showResults && result"
        :data="result"
      />
      <Typo v-else>
        <p class="mb-0 fs-small">
          {{ $t('Pre zobrazenie výsledkov zadajte hľadané slovo.') }}
        </p>
      </Typo>
    </SearchAutocompleteDropdown>

    <!-- Screen reader announcement for search dropdown state and results (WCAG 4.1.2) -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="visually-hidden"
    >
      {{ dropdownAnnouncement }}
    </div>

    <SearchAutocompleteButtonClose
      v-if="dropdownsOpenState.search"
      tabindex="0"
      class="bg-transparent border-0 p-0 mt-5 me-3 text-black-1 position-absolute end-0 top-0"
      @click.prevent="handleClickOutside"
      @focusout="handleClickOutside"
    />
  </form>
  <SearchResultsOverlay v-if="dropdownsOpenState.search" />
</template>

<style>
@import './SearchAutocomplete.vars.scss';
</style>
<style lang="scss" scoped>
@import './SearchAutocomplete.scss';
</style>
